import type React from "react";

import type { LanguageDto } from "@/api/types";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { usePostHog } from "@/external/posthog-client";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { useTranslation } from "@/translations";

type AutoTranslationUserLanguageSupportedModalProps = ModalBaseProps & {
  userLanguage: LanguageDto | undefined;
  onHandled: () => void;
};

/**
 * Prompts users to enable a newly supported content language.
 * `onHandled` runs after Skip or a successful language update, but not when the update fails.
 */
export function AutoTranslationUserLanguageSupportedModal({
  userLanguage,
  isOpened,
  onOpenChange,
  onHandled,
}: AutoTranslationUserLanguageSupportedModalProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const postHog = usePostHog();
  const updateSelfLanguageMutation = selfAccountMutations.useUpdateLanguage();

  if (!userLanguage) {
    return null;
  }

  const handleClickEnable = () => {
    postHog.capture("clicked_auto_translation_user_language_supported_modal_action", {
      action: "enable",
    });

    updateSelfLanguageMutation.mutate(
      {
        uiLanguageId: sessionUser.uiLanguage.id,
        autoTranslationLanguageId: userLanguage.id,
      },
      {
        onSuccess: () => {
          onHandled();
          onOpenChange(false);
        },
      },
    );
  };

  const handleClickSkip = () => {
    postHog.capture("clicked_auto_translation_user_language_supported_modal_action", {
      action: "skip",
    });
    onHandled();
  };

  return (
    <Modal.Root
      data-testid="new-language-supported-modal"
      title={
        <Inline gap="sm" align="center" as="span">
          <Translate01Icon size={24} />
          {userLanguage.introText}
        </Inline>
      }
      badgeComponent={<Badge variant="success">{t("common.label.new")}</Badge>}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc={false}
    >
      <Modal.Body>
        <MultiLineContent>{t("component.auto-translation-user-language-supported-modal.content")}</MultiLineContent>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" onClick={handleClickSkip}>
            {t("common.action.skip")}
          </Button>
        </Modal.Close>
        <Button
          data-testid="enable-translations-btn"
          variant="filled"
          onClick={handleClickEnable}
          isLoading={updateSelfLanguageMutation.isPending}
        >
          {t("component.auto-translation-user-language-supported-modal.btn.enable", {
            language: userLanguage.description,
          })}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
